import { MoreVertical } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

import type { Task } from "../types";
import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";
import type { useTaskActions } from "./useTaskActions";

/** "⋮" menu of a task: postpone, abandon / reopen, edit, delete. */
export const TaskActionsMenu = ({
  task,
  actions,
}: {
  task: Task;
  actions: ReturnType<typeof useTaskActions>;
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [isEditOpen, setEditOpen] = useState(false);
  const itemClassName =
    "cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 cursor-pointer"
            aria-label={translate("resources.tasks.actions.title")}
          >
            <MoreVertical className="size-5 md:size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className={itemClassName}
            onClick={() => actions.postpone(1)}
          >
            {translate("resources.tasks.actions.postpone_tomorrow")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClassName}
            onClick={() => actions.postpone(7)}
          >
            {translate("resources.tasks.actions.postpone_next_week")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClassName}
            onClick={() =>
              actions.setStatus(actions.isClosed ? "todo" : "abandoned")
            }
          >
            {translate(
              actions.isClosed
                ? "resources.tasks.actions.reopen"
                : "resources.tasks.actions.abandon",
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClassName}
            onClick={() => setEditOpen(true)}
          >
            {translate("ra.action.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className={itemClassName}
            onClick={actions.handleDelete}
          >
            {translate("ra.action.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {isMobile ? (
        <TaskEditSheet
          taskId={task.id}
          open={isEditOpen}
          onOpenChange={setEditOpen}
        />
      ) : (
        <TaskEdit
          taskId={task.id}
          open={isEditOpen}
          close={() => setEditOpen(false)}
        />
      )}
    </>
  );
};
